(function () {
    'use strict';

    if (window.DFN_RESULTS_BOOTSTRAPPED) return;
    window.DFN_RESULTS_BOOTSTRAPPED = true;

    const API_BASE = '/apps/tecdoc';
    const STORAGE_KEY = 'dfn_vehicle';
    const REQUEST_CACHE_PREFIX = 'dfn_results_cache:v2:';
    const REQUEST_CACHE_TTL = 15 * 60 * 1000;
    const resultsCache = new Map();
    const INITIAL_DOCUMENT_TITLE = document.title;
    const INITIAL_META_DESCRIPTION = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    const INITIAL_CANONICAL = document.querySelector('link[rel="canonical"]')?.getAttribute('href') || window.location.href;

    function qs(root, selector) {
        return root ? root.querySelector(selector) : null;
    }

    function qsa(root, selector) {
        return root ? Array.from(root.querySelectorAll(selector)) : [];
    }

    function esc(value) {
        return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function readSessionCache(key) {
        try {
            const raw = sessionStorage.getItem(`${REQUEST_CACHE_PREFIX}${key}`);
            if (!raw) return null;
            const cached = JSON.parse(raw);
            if (!cached || cached.expiresAt < Date.now()) {
                sessionStorage.removeItem(`${REQUEST_CACHE_PREFIX}${key}`);
                return null;
            }
            return cached.value || null;
        } catch (_) {
            return null;
        }
    }

    function writeSessionCache(key, value) {
        try {
            sessionStorage.setItem(`${REQUEST_CACHE_PREFIX}${key}`, JSON.stringify({
                value,
                expiresAt: Date.now() + REQUEST_CACHE_TTL
            }));
        } catch (_) { }
    }

    function handleize(value) {
        return String(value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    function normalizeBrandLogoHandle(vendor) {
        const vendorHandle = handleize(vendor);
        if (!vendorHandle) return '';

        if (vendorHandle.includes('bosch')) return 'bosch';
        if (vendorHandle.includes('siemens') || vendorHandle.includes('vdo') || vendorHandle.includes('continental')) return 'siemens-vdo';
        if (vendorHandle.includes('borgwarner') || vendorHandle.includes('kkk') || vendorHandle.includes('schwitzer') || vendorHandle.includes('3k')) return 'borgwarner';
        if (vendorHandle.includes('mahle')) return 'mahle';
        if (vendorHandle.includes('garrett')) return 'garrett';

        switch (vendorHandle) {
            case 'robert-bosch':
            case 'bosch-exchange':
            case 'bosch-diesel-center':
                return 'bosch';
            case 'siemens-vdo':
            case 'siemens-vdo-automotive':
            case 'vdo':
            case 'continental-vdo':
            case 'continental-aftermarket':
            case 'continental':
                return 'siemens-vdo';
            case 'kkk':
            case '3k':
            case 'schwitzer':
                return 'borgwarner';
            case 'mahle-original':
                return 'mahle';
            case 'garrett-motion':
                return 'garrett';
            default:
                return vendorHandle;
        }
    }

    function brandAssetUrl(assetsBase, logoHandle) {
        if (!logoHandle) return '';
        return `${String(assetsBase || '')}brand-${logoHandle}.png`;
    }

    function renderBrandLogoBadge(assetsBase, vendor) {
        const vendorName = String(vendor || '').trim();
        if (!vendorName) return '';

        const logoHandle = normalizeBrandLogoHandle(vendorName);
        if (!logoHandle) return '';

        return `
      <div class="dfn-brand-logo-badge dfn-brand-logo-badge--card" data-brand-logo-badge>
        <img
          src="${esc(brandAssetUrl(assetsBase, logoHandle))}"
          alt="${esc(vendorName)}"
          loading="lazy"
          decoding="async"
          onerror="this.closest('[data-brand-logo-badge]').remove()"
        >
      </div>`;
    }

    function readText(root, key, fallback) {
        const value = root && root.dataset ? root.dataset[key] : '';
        return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;
    }

    function createApiError(response, json, fallbackMessage) {
        const error = new Error((json && json.error) || fallbackMessage || 'API error');
        error.status = response && typeof response.status === 'number' ? response.status : 0;
        error.code = json && json.code ? json.code : '';
        error.isApiError = true;
        return error;
    }

    function isConnectionError(error) {
        const message = String(error && error.message || '').toLowerCase();
        return error instanceof TypeError
            || message.includes('failed to fetch')
            || message.includes('networkerror')
            || message.includes('load failed');
    }

    function isTechnicalApiMessage(message) {
        const value = String(message || '').toLowerCase();
        return value.startsWith('api error')
            || value.includes('http ')
            || value.includes('rate limit')
            || value.includes('too many requests');
    }

    function resolveUserErrorMessage(root, error) {
        const status = Number(error && error.status || 0);
        const message = String(error && error.message || '').trim();
        const generic = readText(root, 'errorText', 'Die Ergebnisse konnten gerade nicht geladen werden. Bitte versuchen Sie es erneut.');

        if (status === 429 || /(^|\D)429(\D|$)/.test(message) || /rate limit|too many requests/i.test(message)) {
            return readText(root, 'apiBusyText', 'Gerade sind zu viele Anfragen offen. Bitte versuchen Sie es in wenigen Sekunden erneut.');
        }

        if (status >= 500 || status === 401 || status === 403) {
            return readText(root, 'apiUnavailableText', 'Der Fahrzeugservice antwortet gerade nicht. Bitte versuchen Sie es in wenigen Minuten erneut.');
        }

        if (status === 404) {
            return readText(root, 'apiNotFoundText', 'Zu dieser Fahrzeugauswahl konnten wir gerade keine Ergebnisse laden. Bitte versuchen Sie es erneut.');
        }

        if (isConnectionError(error)) {
            return readText(root, 'apiConnectionText', 'Die Verbindung zum Fahrzeugservice ist gerade unterbrochen. Bitte versuchen Sie es erneut.');
        }

        if (error && error.isApiError) {
            return generic;
        }

        if (isTechnicalApiMessage(message)) {
            return generic;
        }

        return message || generic;
    }

    function formatText(template, replacements) {
        return Object.entries(replacements || {}).reduce((acc, [key, value]) => {
            return acc.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value == null ? '' : value));
        }, template || '');
    }

    function readBool(value, fallback) {
        if (typeof value === 'boolean') return value;
        if (typeof value !== 'string' || value.trim() === '') return fallback;
        return value === 'true';
    }

    function getConfig(root) {
        return {
            storeId: root.dataset.storeId || 'main-store',
            family: root.dataset.family || '',
            profile: root.dataset.tecDocProfile || 'de',
            language: root.dataset.language || '',
            routeSlug: root.dataset.routeSlug || 'einspritzdusen',
            resultsPageUrl: root.dataset.resultsPageUrl || window.location.pathname,
            currencyCode: root.dataset.currencyCode || 'EUR',
            limit: Number(root.dataset.limit || 48),
            collectionUrl: root.dataset.collectionUrl || `/collections/${root.dataset.routeSlug || 'einspritzdusen'}`,
            assetsBase: root.dataset.assetsBase || '',
            cardShowVendor: readBool(root.dataset.cardShowVendor, false),
            cardShowRating: readBool(root.dataset.cardShowRating, true),
            showDividers: readBool(root.dataset.showDividers, false),
            cardContain: readBool(root.dataset.cardContain, true)
        };
    }

    function resolveRequestProfile(config, value) {
        const configured = String(config.profile || '').trim();
        const requested = String(value || '').trim();
        if (!requested) return configured || 'de';
        return requested;
    }

    function vehicleLabel(vehicle) {
        return [
            vehicle.typeName || vehicle.TypeName || '',
            vehicle.engineCode || vehicle.EngineCode || '',
            vehicle.powerHp || vehicle.PowerHp || '',
            vehicle.yearFrom ? `${vehicle.yearFrom}${vehicle.yearTo ? `-${vehicle.yearTo}` : ''}` : ''
        ].filter(Boolean).join(' \u00B7 ');
    }

    function vehicleDisplayName(vehicle) {
        return [
            vehicle.makeName || vehicle.MakeName || '',
            vehicle.modelName || vehicle.ModelName || '',
            vehicle.typeName || vehicle.TypeName || ''
        ].filter(Boolean).join(' ');
    }

    function getVehicleImageSrc(vehicle) {
        const imageUrl = vehicle && (vehicle.imageUrl || vehicle.ImageUrl || '');
        const value = String(imageUrl || '').trim();
        if (!value) return '';
        if (value.startsWith('/')) return value;
        return `${API_BASE}/vehicle-image?url=${encodeURIComponent(value)}`;
    }

    function openFinder() {
        document.dispatchEvent(new CustomEvent('dfn:open-finder'));

        window.setTimeout(() => {
            const target = qs(document, '[data-dfn-header-shell="true"]')
                || qs(document, '[data-dfn-finder-root="true"]')
                || qs(document, '.dfn-locator');
            if (!target) return;

            const isMobile = window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
            if (!isMobile && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }

            const trigger = qs(target, '[data-dfn-role="header-open"]') || qs(target, '[data-dfn-role="header-change"]');
            if (trigger) trigger.click();

            const input = qs(target, '[data-dfn-role="header-plate-input"]')
                || qs(target, '[data-dfn-role="header-hsn-input"]')
                || qs(target, '[data-dfn-role="make-select"]')
                || qs(target, 'select, input, button');
            if (input && typeof input.focus === 'function') input.focus({ preventScroll: true });
        }, 0);
    }

    function getSavedVehicle() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (_) {
            return null;
        }
    }

    function saveVehicle(result, config) {
        if (!result || !result.vehicle) return;

        const vehicle = {
            ...result.vehicle,
            vehicleId: result.vehicleId || result.vehicle.id || result.vehicle.Id,
            storeId: result.storeId || config.storeId,
            family: result.family || '',
            tecDocProfile: config.profile,
            vehicleName: vehicleDisplayName(result.vehicle),
            savedAt: Date.now()
        };

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
        } catch (_) { }

        document.dispatchEvent(new CustomEvent('dfn:vehicle-selected', { detail: vehicle }));
    }

    function clearSavedVehicle() {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (_) { }

        document.dispatchEvent(new CustomEvent('dfn:vehicle-cleared'));
    }

    function isCompatibleVehicle(vehicle, config) {
        if (!vehicle) return false;
        if (vehicle.storeId && vehicle.storeId !== config.storeId) return false;
        if (vehicle.tecDocProfile && vehicle.tecDocProfile !== config.profile) return false;
        return true;
    }

    async function apiFetch(path) {
        const response = await fetch(`${API_BASE}${path}`);
        let json = null;

        try {
            json = await response.json();
        } catch (_) {
            json = null;
        }

        if (!response.ok) {
            throw createApiError(response, json, `API error ${response.status}`);
        }

        if (!json || json.success === false) {
            throw createApiError(response, json, 'API error');
        }

        return json.data;
    }

    function fetchResults(queryString) {
        const cacheKey = String(queryString || '');
        const persisted = readSessionCache(cacheKey);
        if (persisted) {
            return Promise.resolve(persisted);
        }

        if (!resultsCache.has(cacheKey)) {
            const request = apiFetch(`/vehicle-results?${cacheKey}`)
                .then((data) => {
                    writeSessionCache(cacheKey, data);
                    return data;
                })
                .catch((error) => {
                    resultsCache.delete(cacheKey);
                    throw error;
                });
            resultsCache.set(cacheKey, request);
        }

        return resultsCache.get(cacheKey);
    }

    function formatMoney(amount, currencyCode) {
        try {
            return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
                style: 'currency',
                currency: currencyCode || 'EUR'
            }).format(Number(amount || 0));
        } catch (_) {
            return `${Number(amount || 0).toFixed(2)} ${currencyCode || 'EUR'}`;
        }
    }

    function fallbackLetter(value) {
        const source = String(value || '').trim();
        return source ? source.charAt(0).toUpperCase() : 'F';
    }

    function currentLocalePrefix() {
        const firstSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
        const reserved = new Set(['apps', 'account', 'cart', 'collections', 'pages', 'products', 'search']);
        if (!firstSegment || reserved.has(firstSegment.toLowerCase())) return '';
        return /^[a-z]{2}(?:-[a-z]{2})?$/i.test(firstSegment) ? `/${firstSegment}` : '';
    }

    function localizeStorefrontPath(pathWithQuery) {
        const url = new URL(pathWithQuery, window.location.origin);
        const prefix = currentLocalePrefix();
        if (!prefix || url.pathname.startsWith(`${prefix}/`) || url.pathname === prefix) {
            return `${url.pathname}${url.search}${url.hash}`;
        }

        if (/^\/(?:products|collections|pages|search)(?:\/|\?|$)/.test(url.pathname)) {
            return `${prefix}${url.pathname}${url.search}${url.hash}`;
        }

        return `${url.pathname}${url.search}${url.hash}`;
    }

    function localizeStorefrontUrl(value) {
        const raw = String(value || '').trim();
        if (!raw || raw === '#') return raw || '#';

        try {
            const url = new URL(raw, window.location.origin);
            const pathWithQuery = `${url.pathname}${url.search}${url.hash}`;
            if (url.origin !== window.location.origin) {
                return /^\/(?:products|collections|pages|search)(?:\/|\?|$)/.test(url.pathname)
                    ? localizeStorefrontPath(pathWithQuery)
                    : raw;
            }

            return localizeStorefrontPath(pathWithQuery);
        } catch (_) {
            return raw;
        }
    }

    function buildPageUrl(config, vehicleId, page, family) {
        const url = new URL(config.resultsPageUrl || window.location.pathname, window.location.origin);
        url.searchParams.set('vehicleId', String(vehicleId));
        url.searchParams.set('storeId', config.storeId);
        url.searchParams.set('tecDocProfile', config.profile);

        const selectedFamily = String(family || '').trim();
        if (selectedFamily) {
            url.searchParams.set('family', selectedFamily);
        } else {
            url.searchParams.delete('family');
        }

        if (page > 1) {
            url.searchParams.set('page', String(page));
        } else {
            url.searchParams.delete('page');
        }

        return `${url.pathname}${url.search}`;
    }

    function buildCollectionUrl(config, familySlug) {
        const normalized = String(familySlug || '').trim();
        const baseUrl = new URL(config.collectionUrl || `/collections/${config.routeSlug || 'einspritzdusen'}`, window.location.origin);
        const marker = '/collections/';
        const markerIndex = baseUrl.pathname.indexOf(marker);
        const prefix = markerIndex >= 0 ? baseUrl.pathname.slice(0, markerIndex) : '';
        const targetPath = normalized
            ? `${prefix}${marker}${normalized}`
            : `${prefix}${marker.replace(/\/$/, '')}`;

        return targetPath || '/collections';
    }

    function countLabel(root, count) {
        const value = Number(count || 0);
        return `${value} ${value === 1 ? readText(root, 'productSingularLabel', 'Produkt') : readText(root, 'productPluralLabel', 'Produkte')}`;
    }

    function setMetaDescription(content) {
        const meta = document.querySelector('meta[name="description"]');
        if (!meta) return;
        meta.setAttribute('content', String(content || '').trim());
    }

    function setCanonical(href) {
        const canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) return;
        canonical.setAttribute('href', href);
    }

    function upsertJsonLd(id, payload) {
        let node = document.getElementById(id);
        if (!payload) {
            if (node) node.remove();
            return;
        }

        if (!node) {
            node = document.createElement('script');
            node.type = 'application/ld+json';
            node.id = id;
            document.head.appendChild(node);
        }

        node.textContent = JSON.stringify(payload);
    }

    function shopTitleSuffix(root) {
        const pageTitle = readText(root, 'pageTitle', '');
        if (!pageTitle) return INITIAL_DOCUMENT_TITLE;
        if (INITIAL_DOCUMENT_TITLE.includes(pageTitle)) {
            return INITIAL_DOCUMENT_TITLE.replace(pageTitle, '').replace(/^\s*[\-|–|•:]\s*/, '').trim();
        }

        return '';
    }

    function buildResultsSeoTitle(root, result) {
        if (!result || !result.vehicle) return INITIAL_DOCUMENT_TITLE;

        const parts = [vehicleDisplayName(result.vehicle)];
        if (result.familyName) parts.push(result.familyName);
        parts.push(countLabel(root, result.total));

        const suffix = shopTitleSuffix(root);
        return suffix ? `${parts.join(' | ')} | ${suffix}` : parts.join(' | ');
    }

    function buildResultsSeoDescription(root, result) {
        if (!result || !result.vehicle) {
            return readText(root, 'seoDefaultDescription', INITIAL_META_DESCRIPTION || '');
        }

        const vehicleName = vehicleDisplayName(result.vehicle);
        const familyName = result.familyName || readText(root, 'allFamiliesLabel', 'Alle passenden Teile');
        const countText = formatText(readText(root, 'summaryFoundText', '{count} passende Produkte im Shop gefunden.'), {
            count: result.total || 0
        });

        return `${familyName} für ${vehicleName}. ${countText}`.trim();
    }

    function buildResultsSchema(root, result, config, canonicalUrl) {
        const pageTitle = buildResultsSeoTitle(root, result);
        const pageDescription = buildResultsSeoDescription(root, result);
        const pageName = readText(root, 'pageTitle', 'Fahrzeugergebnisse');
        const items = [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: `${window.location.origin}${currentLocalePrefix() || ''}/`
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: pageName,
                item: canonicalUrl
            }
        ];

        const graph = [
            {
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: pageTitle,
                description: pageDescription
            },
            {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                '@id': `${canonicalUrl}#breadcrumb`,
                itemListElement: items
            }
        ];

        if (result && result.vehicle) {
            const itemListElements = Array.isArray(result.products)
                ? result.products.slice(0, 24).map((product, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    url: new URL(localizeStorefrontUrl(product.url || product.productUrl || canonicalUrl), window.location.origin).toString(),
                    name: product.title || '',
                    image: product.imageUrl || undefined
                }))
                : [];

            if (itemListElements.length) {
                graph.push({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    '@id': `${canonicalUrl}#itemlist`,
                    itemListOrder: 'https://schema.org/ItemListOrderAscending',
                    numberOfItems: Number(result.total || itemListElements.length),
                    itemListElement: itemListElements
                });
            }
        }

        return graph;
    }

    function updateResultsSeo(root, result, config) {
        if (!result || !result.vehicle) {
            document.title = INITIAL_DOCUMENT_TITLE;
            setMetaDescription(INITIAL_META_DESCRIPTION);
            setCanonical(INITIAL_CANONICAL);
            upsertJsonLd('dfn-results-jsonld', null);
            return;
        }

        const canonicalUrl = new URL(
            buildPageUrl(config, result.vehicleId || result.vehicle?.id || result.vehicle?.Id, result.page || 1, result.family),
            window.location.origin
        ).toString();

        document.title = buildResultsSeoTitle(root, result);
        setMetaDescription(buildResultsSeoDescription(root, result));
        setCanonical(canonicalUrl);
        upsertJsonLd('dfn-results-jsonld', buildResultsSchema(root, result, config, canonicalUrl));

        const nextUrl = `${new URL(canonicalUrl).pathname}${new URL(canonicalUrl).search}`;
        if (`${window.location.pathname}${window.location.search}` !== nextUrl) {
            window.history.replaceState({}, '', nextUrl);
        }
    }

    function familyIcon(familySlug) {
        const normalized = String(familySlug || '').trim().toLowerCase();
        if (normalized.includes('turbo')) return 'T';
        if (normalized.includes('nox')) return 'NOx';
        if (normalized.includes('dichtring')) return 'D';
        if (normalized.includes('einspritz') || normalized.includes('injekt')) return 'I';
        return 'P';
    }

    function familyDescription(familySlug) {
        const normalized = String(familySlug || '').trim().toLowerCase();
        if (normalized.includes('turbo')) return 'Turbolader und Aufladungsteile';
        if (normalized.includes('nox')) return 'NOx-Sensoren und Abgassensorik';
        if (normalized.includes('dichtring')) return 'Dichtungen und Montages\u00E4tze';
        if (normalized.includes('einspritz') || normalized.includes('injekt')) return 'Injektoren und Einspritzd\u00FCsen';
        return 'Passende Fahrzeugteile im Shop';
    }

    function renderFamilyPreview(previewProducts, fallback) {
        const previews = Array.isArray(previewProducts)
            ? previewProducts.filter((product) => product && product.imageUrl).slice(0, 2)
            : [];

        if (!previews.length) {
            return `<span class="dfn-results__family-fallback">${esc(fallback)}</span>`;
        }

        return `
      <span class="dfn-results__family-product-stack">
        ${previews.map((product) => `
          <span class="dfn-results__family-product-image">
            <img src="${esc(product.imageUrl)}" alt="${esc(product.title || '')}" loading="lazy">
          </span>
        `).join('')}
      </span>`;
    }

    function roundRating(value) {
        const rating = Number(value || 0);
        if (!Number.isFinite(rating) || rating <= 0) return 0;

        const decimal = rating % 1;
        if (decimal >= 0.3 && decimal <= 0.7) return Math.floor(rating) + 0.5;
        if (decimal > 0.7) return Math.ceil(rating);
        return Math.floor(rating);
    }

    function readNumber(value) {
        if (value === null || value === undefined) return 0;
        if (typeof value === 'object' && value.value !== undefined) return readNumber(value.value);

        const normalized = String(value).trim().replace(',', '.');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    function readProductRatingValue(product) {
        return readNumber(
            product.ratingValue
            ?? product.RatingValue
            ?? product.reviewAverageRating
            ?? product.review_average_rating
            ?? product.rating
            ?? product.Rating
        );
    }

    function readProductRatingCount(product) {
        return readNumber(
            product.ratingCount
            ?? product.RatingCount
            ?? product.reviewCount
            ?? product.review_count
            ?? product.reviewsCount
            ?? product.ReviewsCount
        );
    }

    function readProductSalesCount(product) {
        return readNumber(
            product.salesCount
            ?? product.SalesCount
            ?? product.soldCount
            ?? product.SoldCount
            ?? product.totalSold
            ?? product.TotalSold
            ?? product.unitsSold
            ?? product.UnitsSold
        );
    }

    function readPackSize(product) {
        const direct = readNumber(
            product.packSize
            ?? product.PackSize
            ?? product.quantity
            ?? product.Quantity
        );

        if (Number.isFinite(direct) && direct > 0) return direct;

        const rawSku = String(product && (product.sku || product.Sku) || '').trim();
        const match = rawSku.match(/[-_](\d+)[-_](?:0|50|100)(?:[-_]\d+)?$/i);
        const parsed = match ? readNumber(match[1]) : 0;
        return parsed > 0 ? parsed : 1;
    }

    function normalizeSkuBase(value) {
        const raw = String(value || '').trim();
        if (!raw) return '';

        return raw
            .replace(/(?:[-_]\d+[-_](?:0|50|100))(?:[-_]\d+)?$/i, '')
            .replace(/[-_]+$/g, '')
            .toUpperCase();
    }

    function buildReviewGroupKey(product) {
        const baseSku = normalizeSkuBase(product && (product.sku || product.Sku));
        const packSize = readPackSize(product);
        return baseSku ? `${baseSku}|${packSize}` : '';
    }

    function isBetterRatingSource(candidate, current) {
        if (!current) return true;

        const candidateReviewCount = readProductRatingCount(candidate);
        const currentReviewCount = readProductRatingCount(current);
        if (candidateReviewCount !== currentReviewCount) {
            return candidateReviewCount > currentReviewCount;
        }

        const candidateRatingValue = readProductRatingValue(candidate);
        const currentRatingValue = readProductRatingValue(current);
        if (candidateRatingValue !== currentRatingValue) {
            return candidateRatingValue > currentRatingValue;
        }

        const candidateSalesCount = readProductSalesCount(candidate);
        const currentSalesCount = readProductSalesCount(current);
        if (candidateSalesCount !== currentSalesCount) {
            return candidateSalesCount > currentSalesCount;
        }

        return false;
    }

    function buildRatingSourceMap(products) {
        const byGroup = new Map();

        (Array.isArray(products) ? products : []).forEach((product) => {
            const groupKey = buildReviewGroupKey(product);
            if (groupKey) {
                const currentGroup = byGroup.get(groupKey);
                if (isBetterRatingSource(product, currentGroup)) {
                    byGroup.set(groupKey, product);
                }
            }
        });

        return byGroup;
    }

    function resolveRatingSource(product, ratingSourceMap) {
        const groupKey = buildReviewGroupKey(product);
        return (groupKey && ratingSourceMap && ratingSourceMap.get(groupKey)) || product;
    }

    function renderRating(product, ratingSourceMap) {
        const source = resolveRatingSource(product, ratingSourceMap);
        const ratingValue = readProductRatingValue(source);
        if (!Number.isFinite(ratingValue) || ratingValue <= 0) return '';

        const ratingCount = readProductRatingCount(source);
        const roundedRating = roundRating(ratingValue);
        const ratingPercent = Math.max(0, Math.min(100, (roundedRating / 5) * 100));
        const stars = '&#9733;&#9733;&#9733;&#9733;&#9733;';

        return `
      <div class="dfn-results__card-rating" aria-label="${esc(`${ratingValue.toFixed(1)} von 5`)}">
        <div class="dfn-results__stars" role="img" aria-hidden="true">
          <span class="dfn-results__stars-base">${stars}</span>
          <span class="dfn-results__stars-fill" style="width:${ratingPercent}%;">${stars}</span>
        </div>
        ${ratingCount > 0 ? `<span class="dfn-results__rating-count text-sm">(${esc(ratingCount)})</span>` : ''}
      </div>`;
    }

    function renderCompatibilityBadge(root) {
        const label = readText(root, 'compatibleLabel', 'Passt zu Ihrem Fahrzeug');
        if (!label) return '';

        return `
      <div class="dfn-results__fitment">
        <span>${esc(label)}</span>
      </div>`;
    }

    function displaySku(sku) {
        const raw = String(sku || '').trim();
        if (!raw) return '';

        return raw
            .replace(/(?:[-_]\d+[-_](?:0|50|100))(?:[-_]\d+)?$/i, '')
            .replace(/[-_]+$/g, '');
    }

    function renderSummary(root, result, config) {
        const summary = qs(root, '[data-results-summary]');
        if (!summary) return;

        const vehicle = result && result.vehicle ? result.vehicle : null;
        if (!vehicle) {
            summary.dataset.state = 'empty';
            summary.innerHTML = `
        <div class="dfn-results__summary-main">
          <div class="dfn-results__summary-copy">
            <h2 class="dfn-results__summary-title">${esc(readText(root, 'emptyTitle', 'Kein Fahrzeug ausgew\u00E4hlt'))}</h2>
            <p class="dfn-results__summary-note">${esc(readText(root, 'emptyText', 'Bitte w\u00E4hlen Sie zuerst ein Fahrzeug aus, damit wir passende Produkte laden k\u00F6nnen.'))}</p>
          </div>
          <div class="dfn-results__summary-actions">
            <button type="button" class="btn btn--primary" data-results-open-finder>${esc(readText(root, 'selectVehicleLabel', 'Fahrzeug ausw\u00E4hlen'))}</button>
          </div>
        </div>`;
            bindResultsActions(root, config);
            return;
        }

        const imageUrl = getVehicleImageSrc(vehicle);
        const selectedFamily = String(result.family || '').trim();
        const isLoading = result && result.loading;
        const collectionAction = selectedFamily
            ? `<a class="btn btn--secondary" href="${esc(buildCollectionUrl(config, selectedFamily))}">${esc(result.familyName || readText(root, 'allProductsLabel', 'Alle Produkte'))}</a>`
            : '';
        const foundText = isLoading
            ? readText(root, 'loadingText', 'Wir pr\u00FCfen jetzt die verf\u00FCgbaren Shop-Produkte f\u00FCr Ihr Fahrzeug.')
            : result.total > 0
                ? formatText(readText(root, 'summaryFoundText', '{count} passende Produkte im Shop gefunden.'), { count: result.total })
                : selectedFamily
                    ? formatText(readText(root, 'familyUnavailableText', '{family} ist f\u00FCr dieses Fahrzeug aktuell nicht im Shop verf\u00FCgbar.'), {
                        family: result.familyName || readText(root, 'allProductsLabel', 'Diese Teileart')
                    })
                    : readText(root, 'noProductsText', 'F\u00FCr dieses Fahrzeug sind aktuell keine passenden Shop-Produkte verf\u00FCgbar.');

        summary.dataset.state = isLoading ? 'loading' : result.total > 0 ? 'ready' : 'empty';
        summary.innerHTML = `
      <div class="dfn-results__summary-main">
        <div class="dfn-results__vehicle">
          <div class="dfn-results__vehicle-media">
            ${imageUrl
                ? `<img src="${esc(imageUrl)}" alt="${esc(vehicleDisplayName(vehicle))}" width="160" height="120" loading="lazy">`
                : `<span class="dfn-results__vehicle-fallback">${esc(fallbackLetter(vehicle.makeName || vehicle.MakeName))}</span>`}
          </div>
          <div class="dfn-results__vehicle-copy">
            <p class="dfn-results__vehicle-title">${esc(readText(root, 'summaryDefaultTitle', 'Fahrzeugauswahl'))}</p>
            <p class="dfn-results__vehicle-name">${esc(vehicleDisplayName(vehicle))}</p>
            <p class="dfn-results__vehicle-meta">${esc(vehicleLabel(vehicle))}</p>
            ${isLoading ? `<p class="dfn-results__loading-line">${esc(readText(root, 'loadingTitle', 'Passende Produkte werden geladen'))}</p>` : ''}
            <p class="dfn-results__summary-note">${esc(foundText)}</p>
          </div>
        </div>
        <div class="dfn-results__summary-actions">
          <button type="button" class="btn btn--secondary" data-results-open-finder>${esc(readText(root, 'changeVehicleLabel', 'Fahrzeug \u00E4ndern'))}</button>
          <button type="button" class="btn btn--secondary" data-results-clear>${esc(readText(root, 'resetLabel', 'Fahrzeug zur\u00FCcksetzen'))}</button>
          ${collectionAction}
        </div>
      </div>`;
        bindResultsActions(root, config);
    }

    function renderFamilies(root, result, config) {
        const container = qs(root, '[data-results-families]');
        if (!container) return;

        const families = result && Array.isArray(result.families) ? result.families : [];
        if (families.length <= 1 || !result || !result.vehicleId) {
            container.classList.add('dfn-hidden');
            container.innerHTML = '';
            return;
        }

        const allFamiliesLabel = readText(root, 'allFamiliesLabel', 'Alle passenden Teile');
        const allTotal = Number.isFinite(Number(result.allTotal)) ? Number(result.allTotal) : (result.total || 0);
        const selectedFamily = String(result.family || '').trim();
        container.classList.remove('dfn-hidden');
        container.innerHTML = `
      <div class="dfn-results__family-heading">
        <div>
          <span>${esc(readText(root, 'familyHeadingLabel', 'Produktgruppen f\u00FCr Ihr Fahrzeug'))}</span>
          <strong>${esc(result.familyName || allFamiliesLabel)}</strong>
        </div>
        <small>${esc(countLabel(root, allTotal))}</small>
      </div>
      <div class="dfn-results__family-switcher" role="list" aria-label="Produktgruppen">
        <a class="dfn-results__family-link dfn-results__family-link--all" role="listitem" href="${esc(buildPageUrl(config, result.vehicleId, 1, ''))}" ${selectedFamily ? '' : 'aria-current="true"'}>
          <span class="dfn-results__family-preview">
            <span class="dfn-results__family-fallback">${esc(fallbackLetter(allFamiliesLabel))}</span>
          </span>
          <span class="dfn-results__family-copy">
            <span class="dfn-results__family-name">${esc(allFamiliesLabel)}</span>
            <span class="dfn-results__family-desc">${esc(readText(root, 'allPartsLabel', 'Alle passenden Teile'))}</span>
            <span class="dfn-results__family-count">${esc(countLabel(root, allTotal))}</span>
          </span>
        </a>
        ${families.map((family) => {
            const familySlug = family.family || '';
            const isCurrent = familySlug === selectedFamily;
            return `
            <a class="dfn-results__family-link" role="listitem" href="${esc(buildPageUrl(config, result.vehicleId, 1, familySlug))}" ${isCurrent ? 'aria-current="true"' : ''}>
              <span class="dfn-results__family-preview">
                ${renderFamilyPreview(family.previewProducts, familyIcon(familySlug || family.familyName))}
              </span>
              <span class="dfn-results__family-copy">
                <span class="dfn-results__family-name">${esc(family.familyName)}</span>
                <span class="dfn-results__family-desc">${esc(familyDescription(familySlug || family.familyName))}</span>
                <span class="dfn-results__family-count">${esc(countLabel(root, family.total || 0))}</span>
              </span>
            </a>`;
        }).join('')}
      </div>`;
    }

    function renderError(root, errorMessage) {
        const error = qs(root, '[data-results-error]');
        if (!error) return;

        if (!errorMessage) {
            error.classList.add('dfn-hidden');
            error.removeAttribute('data-tone');
            error.innerHTML = '';
            return;
        }

        error.dataset.tone = 'error';
        error.classList.remove('dfn-hidden');
        error.innerHTML = `
      <strong>${esc(readText(root, 'errorTitle', 'Ergebnisse konnten nicht geladen werden'))}</strong>
      <span>${esc(errorMessage || readText(root, 'errorText', 'Die Ergebnisse konnten gerade nicht geladen werden.'))}</span>
      <button type="button" class="btn btn--secondary" data-results-retry>${esc(readText(root, 'retryLabel', 'Erneut versuchen'))}</button>`;
    }

    function renderToolbar(root, result) {
        const toolbar = qs(root, '[data-results-toolbar]');
        const count = qs(root, '[data-results-count]');
        if (!toolbar || !count) return;

        if (!result || !result.vehicle || Number(result.total || 0) <= 0) {
            toolbar.classList.add('dfn-hidden');
            count.textContent = '';
            return;
        }

        toolbar.classList.remove('dfn-hidden');
        count.textContent = formatText(readText(root, 'toolbarCountText', '{count} passende Produkte'), { count: result.total || 0 });
    }

    function renderGrid(root, result, config) {
        const grid = qs(root, '[data-results-grid]');
        if (!grid) return;

        const products = result && Array.isArray(result.products) ? result.products : [];
        grid.innerHTML = '';

        if (!result || !result.vehicle) {
            return;
        }

        if (!products.length) {
            const selectedFamily = String(result.family || '').trim();
            const allPartsUrl = buildPageUrl(config, result.vehicleId, 1, '');
            const emptyTitle = selectedFamily
                ? formatText(readText(root, 'familyUnavailableTitle', '{family} nicht verf\u00FCgbar'), {
                    family: result.familyName || readText(root, 'allProductsLabel', 'Diese Teileart')
                })
                : readText(root, 'familyEmptyTitle', 'Keine passenden Produkte gefunden');
            const emptyText = selectedFamily
                ? readText(root, 'familyEmptyText', 'F\u00FCr diese Produktgruppe sind aktuell keine ver\u00F6ffentlichten Produkte f\u00FCr Ihr Fahrzeug im Shop vorhanden.')
                : readText(root, 'noProductsText', 'F\u00FCr dieses Fahrzeug sind aktuell keine passenden Shop-Produkte verf\u00FCgbar.');
            const item = document.createElement('li');
            item.className = 'dfn-results__item dfn-results__item--empty';
            item.innerHTML = `
        <div class="dfn-results__empty panel">
          <div class="dfn-results__empty-copy">
            <p class="dfn-results__empty-eyebrow">${esc(readText(root, 'filterActiveLabel', 'Fahrzeugfilter aktiv'))}</p>
            <h2 class="dfn-results__empty-title">${esc(emptyTitle)}</h2>
            <p class="dfn-results__empty-text">${esc(emptyText)}</p>
          </div>
          <div class="dfn-results__empty-actions">
            ${selectedFamily ? `<a class="btn btn--primary" href="${esc(allPartsUrl)}">${esc(readText(root, 'allFamiliesLabel', 'Alle passenden Teile'))}</a>` : ''}
            <button type="button" class="btn btn--secondary" data-results-clear>${esc(readText(root, 'resetLabel', 'Fahrzeug zur\u00FCcksetzen'))}</button>
          </div>
        </div>`;
            grid.appendChild(item);
            bindResultsActions(root, config);
            return;
        }

        const ratingSourceMap = buildRatingSourceMap(products);

        products.forEach((product) => {
            const url = localizeStorefrontUrl(product.url || product.productUrl || '#');
            const cleanSku = displaySku(product.sku);
            const vendorName = product.vendor || product.Vendor || '';
            const imageHtml = product.imageUrl
                ? `<img src="${esc(product.imageUrl)}" alt="${esc(product.title)}" loading="lazy" class="img-fit">`
                : '<span class="dfn-results__vehicle-fallback">P</span>';

            const mediaBadges = [];
            if (product.packSize && Number(product.packSize) > 1) mediaBadges.push(`${esc(product.packSize)}x`);
            if (product.isVehiclePage) mediaBadges.push('Fahrzeugseite');

            const cardClasses = [
                'dfn-results__card',
                'card',
                'card--product',
                'h-full',
                'relative',
                'flex'
            ];

            if (config.cardContain) {
                cardClasses.push('card--product-contained');
            }

            if (!config.showDividers) {
                cardClasses.push('card--no-lines');
            }

            const vendorClass = config.showDividers ? 'card__vendor mb-1 text-sm text-theme-light' : 'card__vendor mb-0 text-sm text-theme-light';
            const titleClass = config.showDividers ? 'card__title font-bold mb-1' : 'card__title font-bold mt-1 mb-0';
            const ratingHtml = config.cardShowRating ? renderRating(product, ratingSourceMap) : '';
            const priceHtml = `
        <div class="price dfn-results__price">
          <span class="price__current">${esc(formatMoney(product.price, config.currencyCode))}</span>
        </div>`;

            const item = document.createElement('li');
            item.className = 'dfn-results__item';
            item.innerHTML = `
        <product-card class="${esc(cardClasses.join(' '))}">
          <div class="card__media relative">
            <a href="${esc(url)}" aria-label="${esc(product.title)}" class="media block relative js-prod-link" style="padding-top: 100%;" tabindex="-1">
              ${imageHtml}
              ${mediaBadges.length
                    ? `<div class="dfn-results__media-badges">${mediaBadges.map((badge) => `<span class="dfn-results__badge dfn-results__badge--media">${badge}</span>`).join('')}</div>`
                    : ''}
              ${renderBrandLogoBadge(config.assetsBase, vendorName)}
            </a>
          </div>
          <div class="card__info-container flex flex-col flex-auto relative">
            <div class="card__info w-full">
              <div class="card__info-inner inline-block w-full">
                ${config.cardShowVendor && vendorName ? `<p class="${vendorClass}">${esc(vendorName)}</p>` : ''}
                ${ratingHtml}
                <p class="${titleClass}">
                  <a href="${esc(url)}" class="card-link text-current js-prod-link">${esc(product.title)}</a>
                </p>
                ${(cleanSku || product.vehicleReference)
                    ? `<p class="dfn-results__product-subline text-sm">
                      ${cleanSku ? `<span class="dfn-results__product-sku">${esc(cleanSku)}</span>` : ''}
                        ${product.vehicleReference ? `<span class="dfn-results__product-reference">${esc(product.vehicleReference)}</span>` : ''}
                      </p>`
                    : ''}
                ${renderCompatibilityBadge(root)}
                ${priceHtml}
              </div>
            </div>
          </div>
        </product-card>`;
            grid.appendChild(item);
        });
    }

    function renderPagination(root, result, config) {
        const pagination = qs(root, '[data-results-pagination]');
        if (!pagination) return;

        pagination.innerHTML = '';

        if (!result || result.totalPages <= 1) {
            pagination.classList.add('dfn-hidden');
            return;
        }

        pagination.classList.remove('dfn-hidden');
        const list = document.createElement('div');
        list.className = 'dfn-results__pagination-list';
        const currentPage = Number(result.page || 1);
        const totalPages = Number(result.totalPages || 1);
        const pages = paginationPages(currentPage, totalPages);

        pages.forEach((page) => {
            if (page === 'ellipsis') {
                const marker = document.createElement('span');
                marker.className = 'dfn-results__pagination-ellipsis';
                marker.textContent = '...';
                list.appendChild(marker);
                return;
            }

            const link = document.createElement('a');
            link.className = 'dfn-results__pagination-link';
            link.href = buildPageUrl(config, result.vehicleId, page, result.family);
            link.textContent = String(page);
            if (page === currentPage) {
                link.setAttribute('aria-current', 'page');
            }
            list.appendChild(link);
        });

        pagination.appendChild(list);
    }

    function paginationPages(currentPage, totalPages) {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }

        const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
        const sorted = Array.from(pages)
            .filter((page) => page >= 1 && page <= totalPages)
            .sort((a, b) => a - b);
        const result = [];

        sorted.forEach((page) => {
            const previous = result[result.length - 1];
            if (typeof previous === 'number' && page - previous > 1) {
                result.push('ellipsis');
            }
            result.push(page);
        });

        return result;
    }

    function bindResultsActions(root, config) {
        qsa(root, '[data-results-open-finder]').forEach((button) => {
            if (button.dataset.dfnBound === 'true') return;
            button.dataset.dfnBound = 'true';
            button.addEventListener('click', openFinder);
        });

        qsa(root, '[data-results-clear]').forEach((button) => {
            if (button.dataset.dfnBound === 'true') return;
            button.dataset.dfnBound = 'true';
            button.addEventListener('click', () => {
                clearSavedVehicle();
                const url = new URL(config.resultsPageUrl || window.location.pathname, window.location.origin);
                window.location.href = url.pathname;
            });
        });

        qsa(root, '[data-results-retry]').forEach((button) => {
            if (button.dataset.dfnBound === 'true') return;
            button.dataset.dfnBound = 'true';
            button.addEventListener('click', () => {
                window.location.reload();
            });
        });
    }

    async function initResults(root) {
        const config = getConfig(root);
        const params = new URLSearchParams(window.location.search);
        const queryProfile = resolveRequestProfile(config, params.get('tecDocProfile'));
        const requestConfig = queryProfile ? { ...config, profile: queryProfile } : config;
        const savedVehicle = getSavedVehicle();
        const requestedVehicleId = params.get('vehicleId') || (savedVehicle && isCompatibleVehicle(savedVehicle, requestConfig) ? String(savedVehicle.vehicleId || savedVehicle.id || '') : '');
        const requestedFamily = params.get('family') || '';
        const page = Math.max(Number(params.get('page') || '1'), 1);

        root.dataset.resultsPageUrl = window.location.pathname;
        bindResultsActions(root, requestConfig);
        renderError(root, '');
        root.setAttribute('aria-busy', 'true');

        if (!requestedVehicleId) {
            renderSummary(root, null, requestConfig);
            renderFamilies(root, null, requestConfig);
            renderToolbar(root, null);
            renderGrid(root, null, requestConfig);
            renderPagination(root, null, requestConfig);
            updateResultsSeo(root, null, requestConfig);
            root.setAttribute('aria-busy', 'false');
            return;
        }

        renderSummary(root, {
            vehicle: savedVehicle && isCompatibleVehicle(savedVehicle, requestConfig) && String(savedVehicle.vehicleId || savedVehicle.id) === String(requestedVehicleId) ? savedVehicle : null,
            total: 0,
            loading: true
        }, requestConfig);

        try {
            const query = new URLSearchParams({
                vehicleId: requestedVehicleId,
                storeId: requestConfig.storeId,
                tecDocProfile: requestConfig.profile,
                displayLocale: config.language || requestConfig.profile,
                page: String(page),
                limit: String(requestConfig.limit)
            });
            if (requestedFamily) query.set('family', requestedFamily);

            const result = await fetchResults(query.toString());
            saveVehicle(result, requestConfig);
            renderSummary(root, result, requestConfig);
            renderFamilies(root, result, requestConfig);
            renderToolbar(root, result);
            renderGrid(root, result, requestConfig);
            renderPagination(root, result, requestConfig);
            updateResultsSeo(root, result, requestConfig);
            root.setAttribute('aria-busy', 'false');
        } catch (error) {
            renderError(root, resolveUserErrorMessage(root, error));
            bindResultsActions(root, requestConfig);
            renderFamilies(root, null, requestConfig);
            renderToolbar(root, null);
            renderGrid(root, null, requestConfig);
            renderPagination(root, null, requestConfig);
            updateResultsSeo(root, null, requestConfig);
            root.setAttribute('aria-busy', 'false');
        }
    }

    function initResultsRoot(root) {
        if (!root || root.dataset.dfnResultsReady === 'true') return;
        root.dataset.dfnResultsReady = 'true';
        initResults(root).catch((error) => console.error('DFN results init error:', error));
    }

    function initAll() {
        qsa(document, '[data-dfn-results-root="true"]').forEach((root) => {
            initResultsRoot(root);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll);
    } else {
        initAll();
    }
})();
